/**
 * Joining a room: the names the matchmaker knows and the options a client sends
 * with `create` / `joinById` (backend.md §3.3, §3.9).
 *
 * Room creation and joining go over Colyseus rather than REST, so these options
 * are the room's real request body and are parsed with the same zod schemas the
 * API uses — Colyseus validates nothing on its own.
 */
import { z } from 'zod';
import { roomConfigSchema } from '../room-config.js';
import { roomCodeSchema } from '../validation/common.js';
import { seatSchema } from './client-messages.js';
/** `gameServer.define()` names. The web client hardcodes nothing else. */
export const MUS_ROOM_NAME = 'mus';
export const LOBBY_ROOM_NAME = 'lobby';
/**
 * Deterministic affordances for e2e and integration runs. Every field is
 * ignored unless the server booted with `TEST_MODE=1`, which `parseEnv` refuses
 * in production (frontend.md §8.2).
 */
export const testRoomOptionsSchema = z.object({
    /** Fixes the engine seed, so a scripted game deals the same cards every run. */
    seed: z.number().int().optional(),
    /** Collapses the 2.5 s / 4 s reveal pause. */
    revealDelayMs: z.number().int().min(0).max(10_000).optional(),
    /** Collapses bot "thinking" delays to a fixed value. */
    botThinkMs: z.number().int().min(0).max(5_000).optional(),
    /**
     * Collapses the pause between two auto-dispatched declarations. Four seats at
     * the real `DECLARE_STEP_MS` is nearly three seconds of every hand, which a
     * game played to 40 cannot afford to sit through.
     */
    declareDelayMs: z.number().int().min(0).max(5_000).optional(),
    /** Overrides the pace tier's per-turn allowance, for timeout tests. */
    baseMs: z.number().int().min(20).max(600_000).optional(),
    /** Overrides the pace tier's reserve, for timeout tests. */
    reserveMs: z.number().int().min(0).max(600_000).optional(),
    /**
     * Overrides the pace tier's reconnection grace. A test that has to watch a
     * grace window *expire* cannot wait the real 45–120 seconds.
     */
    reconnectGraceMs: z.number().int().min(0).max(600_000).optional(),
    /**
     * Forces the `catchable` seña roll instead of drawing one: `0` always catches,
     * `1` never does (the roll succeeds when `roll < senasCatchPct / 100`).
     */
    senasCatchRoll: z.number().min(0).max(1).optional(),
});
/** `client.create(MUS_ROOM_NAME, …)` — the config plus the caller's identity. */
export const createRoomOptionsSchema = roomConfigSchema.extend({
    /** The access JWT. `onAuth` reads it; it never reaches the room state. */
    token: z.string().min(1),
    seat: seatSchema.optional(),
    test: testRoomOptionsSchema.optional(),
});
/** `client.joinById(roomId, …)` and `client.reconnect(…)`. */
export const joinRoomOptionsSchema = z.object({
    token: z.string().min(1),
    seat: seatSchema.optional(),
});
/** `POST /api/rooms/resolve` — a share code in, a Colyseus room id out. */
export const resolveRoomBodySchema = z.object({ code: roomCodeSchema });
/** Built by the client from `PUBLIC_URL`; the server only ever sends the code. */
export function shareLinkFor(publicUrl, code) {
    return `${publicUrl.replace(/\/+$/, '')}/j/${code}`;
}
//# sourceMappingURL=rooms.js.map